import React, { useRef, useEffect } from "react";
//MUI
import { Box, Typography, Button, List, Link } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { motion } from "framer-motion";
import { NotoSansJp, QuickSand } from "./TextStyle";
import DocumentMeta from "react-document-meta";
import { useBranch, Navigate } from "./useBranch";

//素材（動画・画像など）
import BigVideo from "./material/movie.mp4";
import VideoSvg from "./material/movie-object.svg";
import ImageSvg1 from "./material/aboutimg01.svg";
import ImageSvg2 from "./material/aboutimg02.svg";
import ImageSvg3 from "./material/conseptimg.svg";
import BackgroundImage from "./material/conceptbackimg.png";
import Sun from "./material/sun.svg";
import Sunflower from "./material/sunflower.svg";
import ShavedIce from "./material/shaved_ice.svg";
import Watermelon from "./material/watermelon.svg";
import Star from "./material/star.svg";
import Tanabata from "./material/tanabata.svg";
import Matto1 from "./material/matto1.jpg";
import Matto2 from "./material/matto2.jpg";
import Matto3 from "./material/matto3.jpg";
import Matto7 from "./material/matto7.jpg";
import Tobibako1 from "./material/tobibako1.jpg";
import Tobibako2 from "./material/tobibako2.jpg";
import Tobibako3 from "./material/tobibako3.jpg";
import Tetubou1 from "./material/tetubou1.jpg";
import CharacterRed from "./material/character_red.png";
import CharacterBlue from "./material/character_blue.png";
import CharacterBlue2 from "./material/character_blue2.png";
import CharacterGreen from "./material/character_green.png";
import CharacterPurple from "./material/character_purple.png";

const newsIconMap: Record<string, string> = {
  shaved_ice: ShavedIce,
  sunflower: Sunflower,
  sun: Sun,
  watermelon: Watermelon,
  star: Star,
  tanabata: Tanabata,
  "": "",
};

const points = [
  {
    id: 1,
    title: "少人数制レッスン",
    text: "コーチの目がすみずみまで行き届き、練習回数もたっぷり。新しい技は補助付きで安全にチャレンジでき、「できた！」の達成感が毎週積み重なります。",
    image: Tobibako2,
    borderstyle: "dotted 3px #E68ECF",
  },
  {
    id: 2,
    title: "オーダーメイド指導",
    text: "逆上がりを成功させたい、跳び箱を高く飛びたい――目標は子どもごとに違うもの。レベルに合わせて段階的にステップアップするので、無理なく確実に技を習得できます。",
    image: Tetubou1,
    borderstyle: "dotted 3px #93C8E5",
  },
  {
    id: 3,
    title: "苦手克服プログラム",
    text: "走る・跳ぶ・回るなど多彩な基本動作を反復し、神経系を刺激。運動が苦手なお子さまでも、週末ごとに少しずつコツをつかみ、自信あふれる姿へ変わります。",
    image: Tobibako3,
    borderstyle: "dotted 3px #ADD578",
  },
];

const sharedIntroParagraphs = [
  "柔軟性や体幹、瞬発力をバランス良く伸ばし、「できた！」という達成感を毎回積み重ねられるため、学校やほかの習い事とも無理なく両立できます。",
  "失敗しても自分で立ち上がり、次の挑戦へ向かう力を養う――そんなチャレンジ精神あふれる週末の居場所で、笑顔いっぱいの成長を一緒に体験しませんか。",
  "まずはお気軽に体験レッスンへお越しください。",
];

const ClassroomTop = () => {
  const state = useBranch();
  const isSmartphone = useMediaQuery("(max-width:767px)");
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    videoRef.current?.play();
  }, []);

  useEffect(() => {
    document.documentElement.style.overflowX = "hidden";
    document.body.style.overflowX = "hidden";
  }, []);

  useEffect(() => {
    if (!(window as any).instgrm) {
      const script = document.createElement("script");
      script.src = "https://www.instagram.com/embed.js";
      script.async = true;
      document.body.appendChild(script);
    } else {
      (window as any).instgrm.Embeds.process();
    }
  }, []);

  if (state.status === "loading") return null;
  if (state.status === "notfound") return <Navigate to="/" replace />;
  const branch = state.branch;

  const pagelists = [
    ...(branch.showCoursesFeesTaikan
      ? [
          {
            page: "平日【体操式】体幹・体づくり教室と料金",
            url: `/${branch.id}/coursesfees_taikan`,
          },
        ]
      : []),
    ...(branch.showCoursesFees
      ? [{ page: "土日【本格的】体操教室と料金", url: `/${branch.id}/coursesfees` }]
      : []),
    { page: "教室概要", url: `/${branch.id}/classoverview` },
    { page: "よくある質問", url: `/${branch.id}/faq` },
    { page: "ブログ", url: `/${branch.id}/blog` },
    { page: "入会までの流れ", url: `/${branch.id}/join` },
  ];

  const meta = {
    title: branch.top.metaTitle,
    description: branch.top.metaDescription,
    canonical: `https://tht-group.co.jp/${branch.id}`,
    meta: {
      charset: "utf-8",
      name: {
        keywords: [
          ...branch.top.regionKeywords,
          "土日だけ体操教室",
          "バク転",
          "バク転教室",
          "パーソナル教室",
          "体操式メニュー",
          "体操教室",
          "体幹・体づくり教室",
          "柔軟性",
          "体幹",
          "瞬発力",
          "成長フィードバック",
          "マット運動",
          "跳び箱",
          "鉄棒",
          "トランポリン",
          "少人数制",
          "挑戦",
          "成功",
          "無料体験",
          "体験レッスン",
        ].join(","),
      },
    },
  };

  const maskStyles = isSmartphone
    ? {}
    : {
        WebkitMaskImage: `url("${VideoSvg}")`,
        WebkitMaskSize: "contain",
        WebkitMaskPosition: "right",
        WebkitMaskRepeat: "no-repeat",
        maskImage: `url("${VideoSvg}")`,
        maskSize: "contain",
        maskPosition: "right",
        maskRepeat: "no-repeat" as const,
      };

  const ld = branch.top.ld;
  const ldJson = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SportsActivityLocation",
        name: ld.name,
        description: ld.description,
        url: `https://tht-group.co.jp/${branch.id}`,
        image: "https://tht-group.co.jp/og-image.jpg",
        address: {
          "@type": "PostalAddress",
          ...(ld.postalCode ? { postalCode: ld.postalCode } : {}),
          ...(ld.streetAddress ? { streetAddress: ld.streetAddress } : {}),
          addressLocality: ld.addressLocality,
          addressRegion: ld.addressRegion,
          addressCountry: "JP",
        },
        openingHours: ld.openingHours,
        priceRange: ld.priceRange,
      },
      ...(ld.video
        ? [
            {
              "@type": "VideoObject",
              name: ld.video.name,
              description: ld.video.description,
              thumbnailUrl: ld.video.thumbnailUrl,
              contentUrl: ld.video.contentUrl,
              uploadDate: "2024-04-01",
              duration: "PT2M",
            },
          ]
        : []),
    ],
  };

  const introParagraphs = [branch.top.introFirstParagraph, ...sharedIntroParagraphs];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
      />
      <DocumentMeta {...meta}>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              position: "relative",
              top: isSmartphone ? "-60px" : "-5vw",
              width: "100%",
            }}
          >
            <video
              autoPlay
              loop
              muted
              playsInline
              style={{
                maxWidth: "100%",
                width: "100%",
                zIndex: -1,
                ...maskStyles,
              }}
            >
              <source src={BigVideo} type="video/mp4" />
            </video>

            <Box sx={{ position: "absolute", top: 100 }}>
              <List
                sx={{
                  display: isSmartphone ? "none" : "grid",
                  width: "200px",
                  height: "313.98px",
                  textAlign: "left",
                  m: "50px 0 0 30px",
                }}
              >
                {pagelists.map((item, index) => (
                  <Link
                    key={index}
                    href={item.url}
                    sx={{
                      color: "#333333",
                      fontSize: "16px",
                      fontWeight: 600,
                      textDecoration: "none",
                      p: "13px 20px",
                      transition: "color 0.3s ease, transform 0.3s ease",
                      ...NotoSansJp,
                      "&:hover": {
                        color: "#E0B24B",
                        transform: "scale(1.1)",
                      },
                    }}
                  >
                    {item.page}
                  </Link>
                ))}
              </List>
              <Button
                component="a"
                href={branch.freeTrialUrl}
                sx={{
                  display: isSmartphone ? "none" : "block",
                  color: "#FFFFFF",
                  bgcolor: "darkgreen",
                  borderRadius: "50px",
                  p: "15px 40px",
                  fontSize: "16px",
                  transition: "background-color 0.3s ease, transform 0.3s ease",
                  m: "50px 0 0 30px",
                  ...NotoSansJp,
                  "&:hover": {
                    bgcolor: "#E0B24B",
                    transform: "scale(1.1)",
                    overflow: "hidden",
                    maxWidth: "100%",
                  },
                }}
              >
                無料体験
              </Button>
              <Button
                component="a"
                href={branch.membershipUrl}
                sx={{
                  display: isSmartphone ? "none" : "block",
                  color: "#FFFFFF",
                  bgcolor: "darkblue",
                  borderRadius: "50px",
                  p: "15px 40px",
                  fontSize: "16px",
                  transition: "background-color 0.3s ease, transform 0.3s ease",
                  m: "30px 0 0 30px",
                  ...NotoSansJp,
                  "&:hover": {
                    bgcolor: "#E0B24B",
                    transform: "scale(1.1)",
                    overflow: "hidden",
                    maxWidth: "100%",
                  },
                }}
              >
                会員登録
              </Button>
              <Button
                component="a"
                href={branch.loginUrl}
                sx={{
                  display: isSmartphone ? "none" : "block",
                  color: "#FFFFFF",
                  bgcolor: "darkorange",
                  borderRadius: "50px",
                  p: "15px 40px",
                  fontSize: "16px",
                  transition: "background-color 0.3s ease, transform 0.3s ease",
                  m: "30px 0 0 30px",
                  ...NotoSansJp,
                  "&:hover": {
                    bgcolor: "#E0B24B",
                    transform: "scale(1.1)",
                    overflow: "hidden",
                    maxWidth: "100%",
                  },
                }}
              >
                会員ログイン
              </Button>
            </Box>
          </Box>

          <img
            src={CharacterBlue}
            alt="Floating Shapes"
            style={{
              position: "absolute",
              bottom: isSmartphone ? "725px" : "150px",
              right: isSmartphone ? "15px" : "50px",
              width: isSmartphone ? "10%" : "100px",
              zIndex: 3,
            }}
          />
          <motion.img
            src={CharacterPurple}
            alt="green pink yellow"
            style={{
              position: "absolute",
              bottom: isSmartphone ? "650px" : "-250px",
              left: isSmartphone ? "50px" : "200px",
              width: isSmartphone ? "25%" : "250px",
              zIndex: 3,
            }}
            animate={{
              rotate: [-10, 10, -10],
            }}
            transition={{
              duration: 5,
              ease: "easeInOut",
              repeat: Infinity,
            }}
          />
          <Box
            sx={{
              position: "relative",
              bgcolor: "#f7f3d4",
              width: "100vw",
              height: "auto",
              p: isSmartphone ? "7%" : "25% 7%",
              mt: isSmartphone ? "-60px" : "-200px",
              zIndex: -2,
              boxSizing: "border-box",
            }}
          >
            <Grid container direction="column" sx={{ alignItems: "center" }}>
              <Box sx={{ width: isSmartphone ? "85vw" : "60vw" }}>
                <Grid container direction={isSmartphone ? "column" : "row"}>
                  <Grid
                    container
                    direction={isSmartphone ? "column-reverse" : "row"}
                    sx={{ width: "100%" }}
                  >
                    {/* ───────── テキスト部 ───────── */}
                    <Box
                      sx={{
                        position: "relative",
                        width: isSmartphone ? "100%" : "50%",
                        pt: isSmartphone ? "20px" : "100px",
                        pr: isSmartphone ? 0 : "50px",
                        boxSizing: "border-box",
                      }}
                    >
                      <img
                        src={CharacterGreen}
                        alt="green_circle"
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: isSmartphone ? "-140px" : "-550px",
                          zIndex: -1,
                          width: "60%",
                          transform: "rotate(30deg)",
                        }}
                      />
                      <Typography
                        component="h1"
                        sx={{
                          color: "#333333",
                          fontWeight: "bold",
                          fontSize: isSmartphone ? "20px" : "35px",
                          mb: isSmartphone ? "16px" : "24px",
                          mt: isSmartphone ? "20%" : 0,
                          ...NotoSansJp,
                        }}
                      >
                        {branch.roomName}｜土日４つの種目で楽しく「やる気」と「自信」を育てる
                      </Typography>
                      <Box sx={{ pt: 0 }}>
                        {introParagraphs.map((text, i) => (
                          <Typography
                            key={i}
                            sx={{
                              color: "#333333",
                              fontSize: isSmartphone ? "14px" : "16px",
                              fontWeight: 500,
                              mb: "10px",
                              ...NotoSansJp,
                            }}
                          >
                            {text}
                          </Typography>
                        ))}
                      </Box>
                    </Box>

                    {/* ───────── 画像部 ───────── */}
                    <Box
                      sx={{
                        position: "relative",
                        width: isSmartphone ? "100%" : "50%",
                        boxSizing: "border-box",
                        mt: isSmartphone ? "100px" : 0,
                        mb: isSmartphone ? "100px" : 0,
                      }}
                    >
                      <img
                        src={CharacterBlue2}
                        alt="blue_circle"
                        style={{
                          position: "absolute",
                          width: "30%",
                          top: isSmartphone ? "-170px" : 0,
                          left: isSmartphone ? "20px" : 0,
                          zIndex: 5,
                        }}
                      />
                      <img
                        src={Tobibako1}
                        alt="image1"
                        style={{
                          width: "100%",
                          height: isSmartphone ? "300px" : "400px",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 1,
                          objectFit: "cover",
                          WebkitMaskImage: `url("${ImageSvg1}")`,
                          WebkitMaskSize: "contain",
                          WebkitMaskPosition: "center",
                          WebkitMaskRepeat: "no-repeat",
                          maskImage: `url("${ImageSvg1}")`,
                          maskSize: "contain",
                          maskPosition: "center",
                          maskRepeat: "no-repeat",
                        }}
                      />
                      <img
                        src={Matto7}
                        alt="image2"
                        style={{
                          display: isSmartphone ? "none" : "block",
                          width: isSmartphone ? "60%" : "70%",
                          position: "absolute",
                          top: "50%",
                          left: isSmartphone ? "30%" : "40%",
                          transformOrigin: "bottom right",
                          zIndex: 0,
                          WebkitMaskImage: `url("${ImageSvg2}")`,
                          WebkitMaskSize: "contain",
                          WebkitMaskPosition: "center",
                          WebkitMaskRepeat: "no-repeat",
                          maskImage: `url("${ImageSvg2}")`,
                          maskSize: "contain",
                          maskPosition: "center",
                          maskRepeat: "no-repeat",
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Box>
          <Box sx={{ position: "relative" }}>
            <img
              src={Matto3}
              alt="image3"
              style={{
                position: "relative",
                width: "100%",
                marginTop: "-18%",
                WebkitMaskImage: `url("${ImageSvg3}")`,
                WebkitMaskSize: "contain",
                WebkitMaskPosition: "center center",
                WebkitMaskRepeat: "no-repeat",
                maskImage: `url("${ImageSvg3}")`,
                maskSize: "contain",
                maskPosition: "center center",
                maskRepeat: "no-repeat",
                zIndex: 2,
                boxSizing: "border-box",
              }}
            />
          </Box>
          <Box
            sx={{
              backgroundImage: `url(${BackgroundImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              p: isSmartphone ? "50px 24px" : "100px 50px 50px 50px",
              mt: "-16%",
              position: "relative",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {branch.top.weekdaySentence && (
              <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
                <Box sx={{ width: isSmartphone ? "100%" : "50%", mt: "100px" }}>
                  <Grid
                    container
                    direction="column"
                    sx={{ justifyContent: "center", alignItems: "center" }}
                  >
                    <Typography
                      sx={{
                        color: "#333",
                        fontSize: isSmartphone ? "20px" : "25px",
                        fontWeight: "bold",
                        textAlign: "center",
                        ...NotoSansJp,
                      }}
                    >
                      平日は"わたし専用"の体幹タイム
                    </Typography>
                    <Box sx={{ pt: "20px" }}>
                      <Typography
                        sx={{
                          color: "#333333",
                          fontSize: isSmartphone ? "14px" : "16px",
                          fontWeight: 500,
                          mb: "10px",
                          ...NotoSansJp,
                        }}
                      >
                        {branch.top.weekdaySentence}
                        少人数制だから 一人ひとりの「芯」をじっくり強化 できるのが魅力です。
                      </Typography>
                      <Typography
                        sx={{
                          color: "#333333",
                          fontSize: isSmartphone ? "14px" : "16px",
                          fontWeight: 500,
                          mb: "10px",
                          ...NotoSansJp,
                        }}
                      >
                        ・ダイナミックコア：前後/左右/ひねりの三方向から体幹を刺激
                      </Typography>
                      <Typography
                        sx={{
                          color: "#333333",
                          fontSize: isSmartphone ? "14px" : "16px",
                          fontWeight: 500,
                          mb: "10px",
                          ...NotoSansJp,
                        }}
                      >
                        ・バランス＆ステップ：片足保持や素早いステップワークで安定感と俊敏性を向上
                      </Typography>
                      <Typography
                        sx={{
                          color: "#333333",
                          fontSize: isSmartphone ? "14px" : "16px",
                          fontWeight: 500,
                          mb: "10px",
                          ...NotoSansJp,
                        }}
                      >
                        ・フレックスゾーン：関節を大きく動かす連続ストレッチで柔軟性をアップ
                      </Typography>
                      <Typography
                        sx={{
                          color: "#333333",
                          fontSize: isSmartphone ? "14px" : "16px",
                          fontWeight: 500,
                          mb: "10px",
                          ...NotoSansJp,
                        }}
                      >
                        他の習い事とも両立しやすく、 短時間でも効率よく 姿勢・バランス・安定感
                        をレベルアップ。 週末の体操教室と組み合わせれば、技のキレとパワーがさらに加速します。
                      </Typography>
                    </Box>
                    <Box sx={{ pt: "30px" }}>
                      <Button
                        component="a"
                        href={branch.freeTrialUrl}
                        sx={{
                          color: "#FFFFFF",
                          bgcolor: "darkgreen",
                          borderRadius: "50px",
                          p: "15px 40px",
                          fontSize: "16px",
                          transition: "background-color 0.3s ease, transform 0.3s ease",
                          ...NotoSansJp,
                          "&:hover": {
                            bgcolor: "#E0B24B",
                            transform: "scale(1.1)",
                            overflow: "hidden",
                            maxWidth: "100%",
                          },
                        }}
                      >
                        無料体験
                      </Button>
                    </Box>
                  </Grid>
                </Box>
              </Grid>
            )}
            {branch.showCoursesFees && (
              <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
                <Box sx={{ width: isSmartphone ? "100%" : "50%", mt: "100px" }}>
                  <Grid
                    container
                    direction="column"
                    sx={{ justifyContent: "center", alignItems: "center" }}
                  >
                    <Typography
                      sx={{
                        color: "#333",
                        fontSize: isSmartphone ? "20px" : "25px",
                        fontWeight: "bold",
                        textAlign: "center",
                        ...NotoSansJp,
                      }}
                    >
                      土日は"わたし専用"の体操タイム
                    </Typography>
                    <Box sx={{ pt: "20px" }}>
                      <Typography
                        sx={{
                          color: "#333333",
                          fontSize: isSmartphone ? "14px" : "16px",
                          fontWeight: 500,
                          mb: "10px",
                          ...NotoSansJp,
                        }}
                      >
                        {branch.top.weekendSentence}
                        一人ひとりとじっくり向き合える のが魅力です。
                      </Typography>
                      <Typography
                        sx={{
                          color: "#333333",
                          fontSize: isSmartphone ? "14px" : "16px",
                          fontWeight: 500,
                          mb: "10px",
                          ...NotoSansJp,
                        }}
                      >
                        ・運動がちょっと苦手 な子には「できた！」の小さな成功体験を。
                      </Typography>
                      <Typography
                        sx={{
                          color: "#333333",
                          fontSize: isSmartphone ? "14px" : "16px",
                          fontWeight: 500,
                          mb: "10px",
                          ...NotoSansJp,
                        }}
                      >
                        ・動きが早く吸収できる 子には次のステップへの挑戦を。
                      </Typography>
                      <Typography
                        sx={{
                          color: "#333333",
                          fontSize: isSmartphone ? "14px" : "16px",
                          fontWeight: 500,
                          mb: "10px",
                          ...NotoSansJp,
                        }}
                      >
                        ４つの種目（マット・跳び箱・鉄棒・トランポリン）を週末にギュッと凝縮し、子どもたちの
                        やる気 と 自信 を育てます。
                      </Typography>
                    </Box>
                    <Box sx={{ pt: "30px" }}>
                      <Button
                        component="a"
                        href={branch.freeTrialUrl}
                        sx={{
                          color: "#FFFFFF",
                          bgcolor: "darkgreen",
                          borderRadius: "50px",
                          p: "15px 40px",
                          fontSize: "16px",
                          transition: "background-color 0.3s ease, transform 0.3s ease",
                          ...NotoSansJp,
                          "&:hover": {
                            bgcolor: "#E0B24B",
                            transform: "scale(1.1)",
                            overflow: "hidden",
                            maxWidth: "100%",
                          },
                        }}
                      >
                        無料体験
                      </Button>
                    </Box>
                  </Grid>
                </Box>
              </Grid>
            )}
          </Box>
          <Box sx={{ width: "100vw", bgcolor: "#eff9ff" }}>
            <Grid
              container
              direction="column"
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: isSmartphone ? "85%" : "70%",
                  height: "auto",
                  mb: "5vh",
                  mx: "auto",
                  p: isSmartphone ? "50px 0 0 0 " : "100px 0 100px 0",
                  boxSizing: "border-box",
                }}
              >
                <img
                  src={CharacterBlue2}
                  alt="blue_triangle"
                  style={{
                    position: "absolute",
                    top: isSmartphone ? "490px" : "400px",
                    right: isSmartphone ? "30px" : "100px",
                    width: isSmartphone ? "40px" : "10%",
                  }}
                />
                <img
                  src={CharacterRed}
                  alt="pink_triangle"
                  style={{
                    position: "absolute",
                    top: isSmartphone ? "650px" : "765px",
                    right: isSmartphone ? "230px" : "650px",
                    width: isSmartphone ? "80px" : "10%",
                  }}
                />
                <Grid container justifyContent="center" alignItems="flex-end">
                  {points.map((point, index) => (
                    <Grid
                      key={point.id}
                      container
                      direction={
                        isSmartphone ? "column" : index % 2 === 0 ? "row" : "row-reverse"
                      }
                      justifyContent="center"
                      alignItems="center"
                      spacing={3}
                    >
                      <Grid xs={12} sm={5}>
                        <Box
                          component="img"
                          src={point.image}
                          alt={point.title}
                          sx={{ width: "100%", height: "auto", borderRadius: "20px" }}
                        />
                      </Grid>
                      <Grid xs={12} sm={5}>
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            sx={{
                              color: "#333333",
                              fontSize: isSmartphone ? "15px" : "20px",
                              m: isSmartphone ? "0 0 16px 0" : "16px",
                              borderBottom: point.borderstyle,
                              fontWeight: "bold",
                              display: "inline-block",
                              ...QuickSand,
                            }}
                          >
                            POINT {point.id}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#333333",
                              fontSize: isSmartphone ? "20px" : "35px",
                              p: isSmartphone ? "0 0 16px 0" : "16px",
                              fontWeight: "bold",
                              ...NotoSansJp,
                            }}
                          >
                            {point.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: "#333333",
                              fontSize: isSmartphone ? "14px" : "16px",
                              p: isSmartphone ? "0 0 30px 0" : "16px",
                              whiteSpace: "pre-line",
                              fontWeight: 500,
                              ...NotoSansJp,
                            }}
                          >
                            {point.text}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              <Box
                sx={{
                  width: isSmartphone ? "85%" : "70%",
                  p: isSmartphone ? "0 0 50px 0" : "100px 0 200px 0",
                  boxSizing: "border-box",
                }}
              >
                {branch.top.weekdaySentence && (
                  <Grid
                    container
                    direction="column"
                    sx={{ justifyContent: "center", alignItems: "center" }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <motion.img
                        src={CharacterBlue2}
                        alt="blue_triangle"
                        style={{
                          position: "absolute",
                          top: isSmartphone ? "-30px" : "-60px",
                          left: isSmartphone ? "-20px" : "-220px",
                          width: isSmartphone ? "40px" : "25%",
                          zIndex: 3,
                        }}
                        animate={{ rotate: [-10, 10, -10] }}
                        initial={{ rotate: 0, transformOrigin: "center bottom" }}
                        transition={{ duration: 5, ease: "easeInOut", repeat: Infinity }}
                      />
                      <motion.img
                        src={CharacterGreen}
                        alt="green_square&yellow_triangle"
                        style={{
                          position: "absolute",
                          top: isSmartphone ? "-35px" : "-60px",
                          right: isSmartphone ? "-25px" : "-180px",
                          width: isSmartphone ? "45px" : "25%",
                          zIndex: 3,
                        }}
                        animate={{ rotate: [-10, 10, -10] }}
                        initial={{ rotate: 0, transformOrigin: "center bottom" }}
                        transition={{ duration: 5, ease: "easeInOut", repeat: Infinity }}
                      />
                      <Typography
                        sx={{
                          color: "#333333",
                          fontWeight: "bold",
                          fontSize: isSmartphone ? "20px" : "35px",
                        }}
                      >
                        平日【体操式】体幹・体づくり教室と料金について
                      </Typography>
                    </Box>
                    <Box
                      component="img"
                      src={Matto2}
                      alt="image4"
                      sx={{
                        width: isSmartphone ? "100%" : "65%",
                        height: isSmartphone ? "200px" : "auto",
                        objectFit: "cover",
                        borderRadius: "20px",
                        mt: isSmartphone ? "10px" : "30px",
                        boxSizing: "border-box",
                      }}
                    />
                    <Box
                      sx={{
                        width: isSmartphone ? "100%" : "65%",
                        pt: isSmartphone ? "20px" : "30px",
                        boxSizing: "border-box",
                      }}
                    >
                      <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
                        <Box
                          component="a"
                          href={`/${branch.id}/coursesfees_taikan`}
                          sx={{
                            display: "inline-block",
                            textDecoration: "none",
                            borderRadius: "50px",
                            backgroundColor: "darkorange",
                            p: isSmartphone ? "25px 65px" : "20px 100px",
                            textAlign: "center",
                            transition: "transform 0.3s ease-in-out",
                            "&:hover": { transform: "scale(1.1) rotate(5deg)" },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: isSmartphone ? "14px" : "16px",
                              color: "#FFFFFF",
                              fontWeight: 500,
                              ...NotoSansJp,
                            }}
                          >
                            詳しくはこちら
                          </Typography>
                        </Box>
                      </Grid>
                    </Box>
                  </Grid>
                )}
                {branch.showCoursesFees && (
                  <Grid
                    container
                    direction="column"
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      marginTop: "100px",
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <motion.img
                        src={CharacterBlue2}
                        alt="blue_triangle"
                        style={{
                          position: "absolute",
                          top: isSmartphone ? "-30px" : "-60px",
                          left: isSmartphone ? "-20px" : "-160px",
                          width: isSmartphone ? "40px" : "25%",
                          zIndex: 3,
                        }}
                        animate={{ rotate: [-10, 10, -10] }}
                        initial={{ rotate: 0, transformOrigin: "center bottom" }}
                        transition={{ duration: 5, ease: "easeInOut", repeat: Infinity }}
                      />
                      <motion.img
                        src={CharacterGreen}
                        alt="green_square&yellow_triangle"
                        style={{
                          position: "absolute",
                          top: isSmartphone ? "-35px" : "-60px",
                          right: isSmartphone ? "-25px" : "-150px",
                          width: isSmartphone ? "45px" : "25%",
                          zIndex: 3,
                        }}
                        animate={{ rotate: [-10, 10, -10] }}
                        initial={{ rotate: 0, transformOrigin: "center bottom" }}
                        transition={{ duration: 5, ease: "easeInOut", repeat: Infinity }}
                      />
                      <Typography
                        sx={{
                          color: "#333333",
                          fontWeight: "bold",
                          fontSize: isSmartphone ? "20px" : "35px",
                        }}
                      >
                        土日【本格的】体操教室と料金について
                      </Typography>
                    </Box>
                    <Box
                      component="img"
                      src={Matto1}
                      alt="image4"
                      sx={{
                        width: isSmartphone ? "100%" : "65%",
                        height: isSmartphone ? "200px" : "auto",
                        objectFit: "cover",
                        borderRadius: "20px",
                        mt: isSmartphone ? "10px" : "30px",
                        boxSizing: "border-box",
                      }}
                    />
                    <Box
                      sx={{
                        width: isSmartphone ? "100%" : "65%",
                        pt: isSmartphone ? "20px" : "30px",
                        boxSizing: "border-box",
                      }}
                    >
                      <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
                        <Box
                          component="a"
                          href={`/${branch.id}/coursesfees`}
                          sx={{
                            display: "inline-block",
                            textDecoration: "none",
                            borderRadius: "50px",
                            backgroundColor: "darkorange",
                            p: isSmartphone ? "25px 65px" : "20px 100px",
                            textAlign: "center",
                            transition: "transform 0.3s ease-in-out",
                            "&:hover": { transform: "scale(1.1) rotate(5deg)" },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: isSmartphone ? "14px" : "16px",
                              color: "#FFFFFF",
                              fontWeight: 500,
                              ...NotoSansJp,
                            }}
                          >
                            詳しくはこちら
                          </Typography>
                        </Box>
                      </Grid>
                    </Box>
                  </Grid>
                )}
              </Box>
            </Grid>
          </Box>
        </Box>
      </DocumentMeta>
    </>
  );
};

export default ClassroomTop;
